import { AIRPORT_DATABASE } from './airportDatabase';
import { normalizeAirportCode } from './flightRouteUtils';

// Logo sources: Google favicon service where available, DuckDuckGo icons for
// the Enterprise Holdings family + Priceless (Google returns 404 for those domains).
const googleFavicon = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
const duckDuckGoIcon = (domain) => `https://icons.duckduckgo.com/ip3/www.${domain}.ico`;

const RENTAL_COMPANY_BRANDS = [
  { match: 'enterprise', name: 'Enterprise', monogram: 'E', backgroundColor: '#0f6d44', color: '#ffffff', logoUrl: duckDuckGoIcon('enterprise.com') },
  { match: 'hertz', name: 'Hertz', monogram: 'H', backgroundColor: '#facc15', color: '#111827', logoUrl: googleFavicon('hertz.com') },
  { match: 'avis', name: 'Avis', monogram: 'A', backgroundColor: '#dc2626', color: '#ffffff', logoUrl: googleFavicon('avis.com') },
  { match: 'budget', name: 'Budget', monogram: 'B', backgroundColor: '#1d4ed8', color: '#ffffff', logoUrl: googleFavicon('budget.com') },
  { match: 'alamo', name: 'Alamo', monogram: 'A', backgroundColor: '#16a34a', color: '#ffffff', logoUrl: duckDuckGoIcon('alamo.com') },
  { match: 'national', name: 'National', monogram: 'N', backgroundColor: '#15803d', color: '#ffffff', logoUrl: duckDuckGoIcon('nationalcar.com') },
  { match: 'thrifty', name: 'Thrifty', monogram: 'T', backgroundColor: '#2563eb', color: '#ffffff', logoUrl: googleFavicon('thrifty.com') },
  { match: 'dollar', name: 'Dollar', monogram: 'D', backgroundColor: '#22c55e', color: '#052e16', logoUrl: googleFavicon('dollar.com') },
  { match: 'sixt', name: 'Sixt', monogram: 'S', backgroundColor: '#f97316', color: '#111827', logoUrl: googleFavicon('sixt.com') },
  { match: 'europcar', name: 'Europcar', monogram: 'E', backgroundColor: '#16a34a', color: '#ffffff', logoUrl: googleFavicon('europcar.com') },
  { match: 'fox', name: 'Fox', monogram: 'F', backgroundColor: '#2563eb', color: '#ffffff', logoUrl: googleFavicon('foxrentacar.com') },
  { match: 'priceless', name: 'Priceless', monogram: 'P', backgroundColor: '#7c3aed', color: '#ffffff', logoUrl: duckDuckGoIcon('pricelesscarrental.com') },
];

const LOCATION_KEYWORDS = {
  dulles: 'IAD',
  'washington dulles': 'IAD',
  saarinen: 'IAD',
  reagan: 'DCA',
  'ronald reagan': 'DCA',
  "o'hare": 'ORD',
  ohare: 'ORD',
  'love field': 'DAL',
  hobby: 'HOU',
  'george bush': 'IAH',
  'buffalo niagara': 'BUF',
  'niagara international': 'BUF',
  buffalo: 'BUF',
  'genesee street': 'BUF',
  '4200 genesee': 'BUF',
};

const extractLabelKeywords = (label = '') =>
  label
    .split(/[-–]/g)
    .flatMap((segment) => segment.split(/\(/g))
    .map((segment) => segment.replace(/\([A-Z0-9]{3}\)/g, '').trim())
    .filter((segment) => segment.length > 3 && !/^[A-Z0-9]{3}$/.test(segment));

export const inferRentalLocationCode = (locationText) => {
  if (!locationText) {
    return null;
  }

  const trimmed = locationText.trim();
  const lower = trimmed.toLowerCase();

  for (const [keyword, code] of Object.entries(LOCATION_KEYWORDS)) {
    if (lower.includes(keyword)) {
      return code;
    }
  }

  for (const airport of AIRPORT_DATABASE) {
    const city = airport.city.toLowerCase();
    if (city.length > 2 && lower.includes(city)) {
      // Avoid matching "New York" in addresses like "Buffalo, New York"
      if (city === 'new york' && lower.includes('buffalo')) {
        continue;
      }
      return airport.iata;
    }

    for (const keyword of extractLabelKeywords(airport.label)) {
      if (lower.includes(keyword.toLowerCase())) {
        return airport.iata;
      }
    }
  }

  if (/^[A-Za-z0-9]{3}$/.test(trimmed) || /\([A-Za-z0-9]{3}\)/.test(trimmed)) {
    return normalizeAirportCode(trimmed);
  }

  return null;
};

export const isCarRentalItineraryItem = (item) =>
  item?.category === 'transport' && item?.transportation_type === 'car_rental';

export const getRentalCompanyName = (item) =>
  item?.transportation_data?.rental_company || item?.transportation_data?.company || null;

const normalizeCompanyName = (company = '') =>
  company.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const buildCompanyMonogram = (company = '') => {
  const words = normalizeCompanyName(company).split(' ').filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  if (words.length === 1) {
    return words[0].slice(0, 1).toUpperCase();
  }
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

export const getRentalCompanyLogoBrand = (item) => {
  const company = getRentalCompanyName(item);
  if (!company) {
    return null;
  }

  const normalizedCompany = normalizeCompanyName(company);
  const matchedBrand = RENTAL_COMPANY_BRANDS.find((brand) => normalizedCompany.includes(brand.match));

  if (matchedBrand?.logoUrl) {
    const { match, ...brand } = matchedBrand;
    return brand;
  }

  return null;
};

export const getRentalCompanyBrand = (item) => {
  const company = getRentalCompanyName(item);
  if (!company) {
    return null;
  }

  const matchedBrand = getRentalCompanyLogoBrand(item);
  if (matchedBrand) {
    return matchedBrand;
  }

  return {
    name: company,
    monogram: buildCompanyMonogram(company),
    backgroundColor: '#e2e8f0',
    color: '#1e293b',
  };
};

export const getRentalRouteParts = (item) => {
  if (!isCarRentalItineraryItem(item)) {
    return null;
  }

  const data = item?.transportation_data || {};
  const pickupSource = data.pickup_location || data.pickup_airport || item.location;
  const dropoffSource = data.dropoff_location || data.dropoff_airport;

  let from =
    normalizeAirportCode(data.pickup_airport) ||
    inferRentalLocationCode(data.pickup_location) ||
    inferRentalLocationCode(item.location);
  let to =
    normalizeAirportCode(data.dropoff_airport) ||
    inferRentalLocationCode(data.dropoff_location);

  if (!from && pickupSource) {
    from = inferRentalLocationCode(pickupSource);
  }
  if (!to && dropoffSource) {
    to = inferRentalLocationCode(dropoffSource);
  }

  if (from && to && from === to) {
    return { from, to: null };
  }

  if (from) {
    return { from, to: to || null };
  }

  return null;
};

export const getRentalRouteLabel = (item) => {
  const route = getRentalRouteParts(item);
  if (!route?.from) {
    return null;
  }

  if (route.to) {
    return `${route.from} → ${route.to}`;
  }

  return route.from;
};

export const getRentalCardTitle = (item) => {
  const routeLabel = getRentalRouteLabel(item);
  if (routeLabel) {
    return routeLabel;
  }

  const carType = item?.transportation_data?.car_type;
  if (carType && carType !== 'Standard') {
    return `${carType} Rental`;
  }

  if (item?.name && item.name.toLowerCase() !== 'car rental') {
    return item.name;
  }

  return item?.description || item?.location || 'Car Rental';
};

export const getRentalCardSubtitle = (item) => {
  const routeLabel = getRentalRouteLabel(item);
  if (!routeLabel) {
    return null;
  }

  const company = getRentalCompanyName(item);
  if (company) {
    return company;
  }

  const carType = item?.transportation_data?.car_type;
  if (carType && carType !== 'Standard') {
    return `${carType} Rental`;
  }

  if (item?.name && !['car rental', 'car'].includes(item.name.toLowerCase().trim())) {
    return item.name;
  }

  return null;
};
